import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import PlayPause from "./components/PlayPause";
import JumpToNextTimestamp from "./components/JumpToNextTimestamp";
import JumpToPreviousTimestamp from "./components/JumpToPreviousTimestamp";
import EnterExitFullscreen from "./components/EnterExitFullscreen";
import VideoProgressBar from "./components/progress-bar/VideoProgressBar";
import TrackDetails from "./components/TrackDetails";
import TimeDetails from "./components/TimeDetails";
import DetachVideo from "./components/DetachVideo";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import HighlightSettings from "./components/HighlightSettings";
import { HighlightDetectedDamagesFilter } from "./components/HighlightDetectedDamagesFilter";
import { featureFlagsStorage } from "../../services/feature-flags/FeatureFlagsStorage";
import { Flags } from "../../generated/FeatureFlags";
import { Segmentation } from "../../api-models/response/VideoAnalysisDataResponse";
import { SegmentationService } from "../../services/segmentation/SegmentationService";

interface VideoControlsProps {
  videoSegmentation?: SegmentationService;
  filteredSegmentations?: Segmentation[];
}

export default observer(function VideoControls(props: VideoControlsProps) {
  const { isVideoPlaying, isPictureInPicture, isFullscreen, enterFullscreen, exitFullscreen } =
    videoPlayerService;
  const { videoSegmentation, filteredSegmentations } = props;

  const shouldDisplayHighlightDetectionStatus = useMemo(
    () =>
      featureFlagsStorage.isFeatureEnabled(Flags.Show_Video_Segmentations) &&
      (videoSegmentation?.segmentationRunning || videoSegmentation?.segmentationFailed),
    [videoSegmentation?.segmentationFailed, videoSegmentation?.segmentationRunning]
  );

  const shouldDisplayHighlightDetectionSettings = useMemo(
    () =>
      featureFlagsStorage.isFeatureEnabled(Flags.Show_Video_Segmentations) &&
      videoSegmentation?.segmentationSuccessful,
    [
      videoSegmentation?.segmentationSuccessful
    ]
  );

  return (
    <section className="video-controls">
      <VideoProgressBar
        videoSegmentation={videoSegmentation}
        filteredSegmentations={filteredSegmentations}
      />
      <div className="video-buttons">
        <div className="position-left">
          <PlayPause
            tooltipTextKey={isVideoPlaying ? "VideoControls.pause" : "VideoControls.play"}
          />
          <TimeDetails />
        </div>
        <div className="position-center" style={{ display: 'none' }}>
          <JumpToPreviousTimestamp tooltipTextKey="VideoControls.previous" />
          <TrackDetails />
          <JumpToNextTimestamp tooltipTextKey="VideoControls.next" />
        </div>
        <div className="position-right">
          {shouldDisplayHighlightDetectionStatus && (
            <HighlightDetectedDamagesFilter videoSegmentation={videoSegmentation} />
          )}
          {shouldDisplayHighlightDetectionSettings && (
            <HighlightSettings tooltipTextKey="VideoAnalysis.video-settings.tooltip" />
          )}
          <DetachVideo
            tooltipTextKey={
              isPictureInPicture ? "VideoControls.exit-detach" : "VideoControls.detach"
            }
          />
          <EnterExitFullscreen
            enter={enterFullscreen}
            exit={exitFullscreen}
            isFullscreen={isFullscreen}
            tooltipTextKey={
              isFullscreen ? "VideoControls.exit-fullscreen" : "VideoControls.enter-fullscreen"
            }
          />
        </div>
      </div>
    </section>
  );
});
